import { Auth } from "aws-amplify";
import store from "../store";
import { LoginEnum } from "../Redux/Users/UserTypes";

// Cache the Cognito ID token between player requests. The player calls the
// delegate before every authenticated request, and an uncached
// Auth.currentSession() rebuilds the CognitoUser each call and can degrade to
// a Cognito GetUser network round-trip — a rate-limit risk on chatty players.
const REFRESH_MARGIN_S = 30;
let cachedIdToken = null;

export const clearPlayerAuthTokenCache = () => {
  cachedIdToken = null;
};

const getCognitoIdToken = async () => {
  const nowS = Math.floor(Date.now() / 1000);
  if (cachedIdToken && cachedIdToken.exp - REFRESH_MARGIN_S > nowS) {
    return cachedIdToken.jwt;
  }

  const session = await Auth.currentSession();
  const idToken = session.getIdToken();
  cachedIdToken = { jwt: idToken.getJwtToken(), exp: idToken.getExpiration() };
  return cachedIdToken.jwt;
};

const userQualifies = (User) =>
  User.loginStatus === LoginEnum.isLoggedIn && !User.isLtiUser;

/**
 * Returns a getAuthToken delegate for a Player-2 mount, or undefined when the
 * player should keep its legacy self-signing (OUZ-179, V2 parity).
 *
 * Only a signed-in, non-LTI user's player traffic moves onto their own
 * VIVED-pool Cognito ID token. Anonymous viewers (public embeds, simple-access
 * kiosks) and LTI launches get no delegate, so the player self-signs from its
 * playerId exactly as it does in production today — several player API routes
 * (e.g. resourcesforplayer) reject unauthenticated requests at the gateway, so
 * withholding the delegate is what keeps those surfaces working.
 *
 * The delegate re-checks the signed-in state on every call and fails closed if
 * the user signed out or an LTI takeover landed after the mount. It never
 * falls back to a token for a different principal.
 */
export const getPlayerAuthTokenDelegate = () => {
  if (!userQualifies(store.getState().User)) {
    return undefined;
  }

  return async () => {
    if (!userQualifies(store.getState().User)) {
      throw new Error("User is no longer signed in as a non-LTI user");
    }
    return getCognitoIdToken();
  };
};
